export const RANK_STEP = 1024;
export const RANK_MIN_GAP = 2 ** -20;
export const RANK_MAX_ABS = 2 ** 40;

export interface Ranked {
  id: string;
  rank: number;
}

/** Order inside a cell: rank ascending, then id so two devices agree on ties. */
export function compareRanked(a: Ranked, b: Ranked): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function sortByRank<T extends Ranked>(items: readonly T[]): T[] {
  return [...items].sort(compareRanked);
}

export function rankBetween(before: number | null, after: number | null): number {
  if (before === null && after === null) return RANK_STEP;
  if (before === null) return (after as number) - RANK_STEP;
  if (after === null) return before + RANK_STEP;
  return (before + after) / 2;
}

export function rankIsSafe(before: number | null, after: number | null, rank: number): boolean {
  if (!Number.isFinite(rank) || Math.abs(rank) > RANK_MAX_ABS) return false;
  if (before !== null && rank - before < RANK_MIN_GAP) return false;
  if (after !== null && after - rank < RANK_MIN_GAP) return false;
  return true;
}

export interface InsertPlan {
  /** Rank for the moving card. */
  rank: number;
  /** When the cell had to be renormalized: every card of the cell, moving one included, with its new rank. */
  renormalized: Ranked[] | null;
}

/**
 * `others` is the target cell sorted, without the moving card; `index` is where it lands.
 * Midpoint insertion until the gap gets too small or the numbers too large, then the whole
 * cell is respaced. Renormalization is rare and local to one cell.
 */
export function planInsert(others: readonly Ranked[], index: number, movingId: string): InsertPlan {
  const i = Math.max(0, Math.min(index, others.length));
  const before = i > 0 ? (others[i - 1] as Ranked).rank : null;
  const after = i < others.length ? (others[i] as Ranked).rank : null;
  const rank = rankBetween(before, after);
  if (rankIsSafe(before, after, rank)) return { rank, renormalized: null };
  const ids = [...others.slice(0, i).map((o) => o.id), movingId, ...others.slice(i).map((o) => o.id)];
  const renormalized = ids.map((id, k) => ({ id, rank: RANK_STEP * (k + 1) }));
  return { rank: RANK_STEP * (i + 1), renormalized };
}
